import { testimonialsPageSchema, breadcrumbSchema } from '../utils/structuredData';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Quote, MapPin, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';
import Accordion from '../components/Accordion';

const TestimonialsPage = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={styles.page}>
      <style>{`
        @media (max-width: 768px) {
          .test-hero-h1 { font-size: 32px !important; letter-spacing: 1px !important; }
          .test-hero-sub { font-size: 15px !important; }
          .test-hero-section { padding: 60px 0 !important; }
          .test-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
          .test-stats-h2 { font-size: 26px !important; letter-spacing: 1px !important; }
          .test-stats-num { font-size: 44px !important; }
          .test-cta-h2 { font-size: 26px !important; }
          .test-cta-text { font-size: 16px !important; }
          .test-btn { font-size: 18px !important; padding: 16px 20px !important; }
          .test-section-pad { padding: 60px 0 !important; }
          .test-card { padding: 24px 20px !important; }
        }
      `}</style>
      
      <SEO
        title="Dealer Testimonials — 250+ Success Stories"
        description="Real testimonials from 250+ dealerships. Car, golf cart, motorcycle & RV dealers share documented results — appointments booked, units sold, gross profit."
        keywords="dealer testimonials, automotive event reviews, dealership event success stories, golf cart dealer testimonials, motorcycle dealer events review, car dealer event marketing reviews"
        canonicalUrl="https://driven2win.com/testimonials"
        structuredData={[testimonialsPageSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Testimonials',url:'https://driven2win.com/testimonials'}])]}
      />
      
      {/* Hero */}
      <section style={styles.hero} className="test-hero-section">
        {/* Video background — swap /videos/testimonials-bg.mp4 once downloaded */}
        <VideoBackground
          src="/videos/testimonials-bg.mp4"
          fallbackImage="/images/hero/testimonials-fallback.jpg"
          posterImage="/images/hero/testimonials-poster.jpg"
          overlayGradient="linear-gradient(135deg, rgba(26,31,58,0.80) 0%, rgba(15,17,24,0.85) 100%)"
        />
        <div style={{...styles.container, position:'relative', zIndex:1}}>
          <h1 style={styles.heroH1} className="test-hero-h1">
            WHAT DEALERS ARE <span style={{color: '#FFC300'}}>SAYING</span>
          </h1>
          <p style={styles.heroSubheadline} className="test-hero-sub">
            Don't take our word for it — hear from dealers across America who trust Driven2WIN to deliver MASSIVE results
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section style={styles.testimonialsSection} className="test-section-pad">
        <div style={styles.container}>
          {isMobile ? (
            <Accordion
              items={testimonials.map(t => ({
                title: `${t.title} — ${t.location}`,
                content: (
                  <>
                    <div style={{...styles.testimonialQuote, color: '#FFFFFF'}}>
                      <Quote size={32} color="#FFC300" style={{marginBottom: '12px'}} />
                      <p style={{fontSize: '15px', lineHeight: '1.6', marginBottom: '16px'}}>"{t.quote}"</p>
                    </div>
                    {t.title && <p style={{fontSize: '13px', color: '#E5E5E5', marginBottom: '4px'}}><strong>{t.title}</strong></p>}
                    <p style={{fontSize: '13px', color: '#999'}}>{t.location}</p>
                  </>
                )
              }))}
            />
          ) : (
            <div className="test-grid" style={styles.testimonialsGrid}>
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} {...testimonial} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Banner */}
      <section style={styles.statsSection} className="test-section-pad">
        <div style={styles.container}>
          <h2 style={styles.statsH2} className="test-stats-h2">
            TRUSTED BY <span style={{color: '#FFC300'}}>250+ DEALERS</span> NATIONWIDE
          </h2>
          <div className="grid-3col" style={styles.statsGrid}>
            <StatItem number="23+" label="Years in Business" />
            <StatItem number="6,500+" label="Events Produced" />
            <StatItem number="50" label="States Served" />
            <StatItem number="98%" label="Client Satisfaction" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection} className="test-section-pad">
        <div style={styles.container}>
          <h2 style={styles.ctaH2} className="test-cta-h2">
            Ready to Join Our <span style={{color: '#FFC300'}}>SUCCESS STORIES?</span>
          </h2>
          <p style={styles.ctaText} className="test-cta-text">
            Let's create an event that YOU'LL be raving about to your colleagues!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary} className="test-btn">
            Schedule Your FREE Event Strategy Call
            <ArrowRight size={24} style={{marginLeft: '12px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const TestimonialCard = ({ quote, name, title, company, location, results }) => (
  <div style={styles.testimonialCard}>
    <div style={styles.quoteIcon}>
      <Quote size={48} color="#FFC300" />
    </div>
    
    <div style={styles.starRating}>
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={20} fill="#FFC300" color="#FFC300" />
      ))}
    </div>
    
    <p style={styles.quote}>"{quote}"</p>
    
    {results && (
      <div style={styles.results}>
        <div style={styles.resultsTitle}>Event Results:</div>
        <div style={styles.resultsText}>{results}</div>
      </div>
    )}
    
    <div style={styles.author}>
      {name && <div style={styles.authorName}>{name}</div>}
      {title && <div style={styles.authorTitle}>{title}</div>}
      {company && <div style={styles.authorCompany}>{company}</div>}
      <div style={styles.authorLocation}>
        <MapPin size={14} />
        <span>{location}</span>
      </div>
    </div>
  </div>
);

const StatItem = ({ number, label }) => (
  <div style={styles.statItem}>
    <div style={styles.statNumber}>{number}</div>
    <div style={styles.statLabel}>{label}</div>
  </div>
);

// Data
const testimonials = [
  {
    quote: "Driven2WIN brought us 127 appointments in 5 days and we sold 67 units with $407K in gross! This was THE BEST event we've ever run. The team is professional, the results are REAL, and we're booking them again next quarter.",
    name: "",
    title: "General Manager",
    company: "",
    location: "Tampa, FL",
    results: "127 appointments • 67 units sold • $407K gross"
  },
  {
    quote: "I've worked with a lot of event companies over my 20 years in this business. Driven2WIN is hands-down the best. They handle EVERYTHING — permits, setup, promotion — and they GUARANTEE their numbers. We hit 84 units sold in one week!",
    name: "",
    title: "Owner",
    company: "",
    location: "Dallas, TX",
    results: "156 appointments • 84 units sold • $457K gross"
  },
  {
    quote: "What impressed me most was the TURNOUT. Every hour we had fresh customers walking in with the mailer. The energy was incredible. Our sales team was closing deals left and right. This is what a REAL event looks like!",
    name: "",
    title: "Sales Director",
    company: "",
    location: "Phoenix, AZ",
    results: "118 appointments • 94 units sold • $523K gross"
  },
  {
    quote: "We were skeptical at first — we'd been burned by other event companies before. But Driven2WIN delivered EXACTLY what they promised. The direct mail campaign generated massive response and the event was professionally executed from start to finish.",
    name: "",
    title: "General Manager",
    company: "",
    location: "Charlotte, NC",
    results: "98 appointments • 59 units sold • $367K gross"
  },
  {
    quote: "The Facebook campaign they created went VIRAL. We had 340K impressions and our phones were ringing off the hook! I've never seen this level of engagement from a social media campaign. Absolutely worth every penny.",
    name: "",
    title: "Marketing Director",
    company: "",
    location: "Denver, CO",
    results: "104 appointments • 83 units sold • $502K gross"
  },
  {
    quote: "What sets Driven2WIN apart is their ATTENTION TO DETAIL. They obtained all the permits, coordinated with the venue, installed the tents professionally, and even stayed on-site during the event to handle any issues. Zero stress for us.",
    name: "",
    title: "Owner",
    company: "",
    location: "Atlanta, GA",
    results: "112 appointments • 68 units sold • $386K gross"
  },
  {
    quote: "Their AI appointment setting system is INCREDIBLE. Customers could book appointments 24/7, and our calendar was completely PACKED before the event even started. This technology gives you a huge competitive advantage.",
    name: "",
    title: "Sales Manager",
    company: "",
    location: "Houston, TX",
    results: "134 appointments • 77 units sold • $437K gross"
  },
  {
    quote: "We specialize in golf carts and thought offsite events wouldn't work for us. We were WRONG! Driven2WIN helped us reach retirement communities and upscale neighborhoods where golf cart ownership is huge. Best ROI we've ever seen.",
    name: "",
    title: "Owner",
    company: "",
    location: "Orlando, FL",
    results: "89 appointments • 73 units sold • $441K gross"
  },
  {
    quote: "The mailers were BEAUTIFUL — high-quality design that looked like a major event announcement, not cheap junk mail. Response rate was 3.2%, which is TRIPLE what we've gotten from other companies!",
    name: "",
    title: "Marketing Manager",
    company: "",
    location: "Nashville, TN",
    results: "118 appointments • 87 units sold • $498K gross"
  },
  {
    quote: "I can't say enough good things about this team. They're responsive, professional, and they DELIVER. Our event crushed our sales goals and we're already planning the next one with them.",
    name: "",
    title: "General Manager",
    company: "",
    location: "Seattle, WA",
    results: "93 appointments • 72 units sold • $467K gross"
  },
  {
    quote: "The combination of direct mail + social media created MASSIVE AWARENESS in our market. People were talking about our event everywhere — Facebook, Instagram, even local news picked it up. The buzz was unreal!",
    name: "",
    title: "Owner",
    company: "",
    location: "Miami, FL",
    results: "141 appointments • 72 units sold • $419K gross"
  },
  {
    quote: "After 15 years in the automotive business, I thought I'd seen it all. Driven2WIN proved me wrong. Their systematic approach and proven processes delivered results that exceeded our highest expectations.",
    name: "",
    title: "General Sales Manager",
    company: "",
    location: "Kansas City, MO",
    results: "106 appointments • 81 units sold • $478K gross"
  },
  {
    quote: "We'd been doing traditional lot sales for years and hit a ceiling. Driven2WIN came in, set up a professional offsite event near a Scottsdale retirement community, and we moved 43 carts in four days. The direct mail targeting was spot-on — our ideal customers showed up ready to buy. We're already booking our next event.",
    name: "",
    title: "Owner",
    company: "",
    location: "Scottsdale, AZ",
    results: "58 appointments • 43 units sold • $268K gross"
  },
  {
    quote: "Golf cart sales in the bayou communities around here are HUGE, but we never had a way to reach those buyers at scale. Driven2WIN built us a targeted direct mail campaign and set up an offsite event near a lakefront community. Sold 41 carts over a long weekend — our best event ever. These guys know what they're doing.",
    name: "",
    title: "General Manager",
    company: "",
    location: "Baton Rouge, LA",
    results: "54 appointments • 41 units sold • $255K gross"
  },
];

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: '#0F1118',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(227, 30, 36, 0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.7',
    position: 'relative',
    zIndex: 1,
  },
  testimonialsSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  testimonialsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
    gap: '32px',
  },
  testimonialCard: {
    background: '#FFFFFF',
    borderRadius: '20px',
    padding: '40px 36px',
    position: 'relative',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  },
  quoteIcon: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    opacity: 0.3,
  },
  starRating: {
    display: 'flex',
    gap: '6px',
    marginBottom: '20px',
  },
  quote: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#333',
    marginBottom: '24px',
    fontStyle: 'italic',
  },
  results: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '24px',
  },
  resultsTitle: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '12px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '700',
    marginBottom: '6px',
  },
  resultsText: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '18px',
    color: '#FFFFFF',
    fontWeight: '600',
  },
  author: {
    borderTop: '2px solid #F5F5F5',
    paddingTop: '20px',
  },
  authorName: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    color: '#1A1F3A',
    letterSpacing: '1px',
    marginBottom: '4px',
  },
  authorTitle: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
    marginBottom: '2px',
  },
  authorCompany: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#999',
    marginBottom: '6px',
  },
  authorLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '13px',
    color: '#999',
  },
  statsSection: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '100px 0',
    textAlign: 'center',
  },
  statsH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '64px',
    letterSpacing: '2px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '48px',
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '64px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '8px',
  },
  statLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  ctaSection: {
    background: '#1A1F3A',
    padding: '100px 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    marginBottom: '48px',
    fontWeight: '600',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(227, 30, 36, 0.4)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default TestimonialsPage;
